<template>
  <div class="search-container" ref="searchContainer">
    <div style="display: flex; flex-direction: row; align-content: space-between; gap: 10px">
      <div style="margin: auto">
        <button v-if="!isOrdinaryUser" class="update-button" @click="updateMessages" :disabled="isPollingPaused">
          <span v-if="isPollingPaused" class="spinner"></span>
          <span v-else>Update Messages</span>
        </button>
      </div>
      <div class="input-wrapper">
        <input
          v-model="searchQuery"
          @input="handleSearch"
          type="text"
          placeholder="Search messages..."
          class="search-input"
          @focus="handleFocus"
        />
        <button
          v-if="searchQuery"
          class="clear-button"
          @click="closeDropdown"
          aria-label="Clear search"
        >
          ✖
        </button>
      </div>
    </div>

    <div v-if="isSearching" class="lds-grid" style="justify-items: center">
      <div class="lds-ripple"><div></div><div></div></div>
      <p>Searching messages...</p>
    </div>

    <div v-if="showDropdown && searchResults.length" class="dropdown">
      <div
        v-for="result in searchResults"
        :key="result.message.id"
        class="result-item"
        @click="selectMessage(result.message.id)"
      >
        <span class="sender">{{ result.sender }}</span>
        <span class="message">
          <span
            v-for="(part, idx) in highlightSearchTermParts(result.message.body)"
            :key="idx"
            :class="{ highlight: part.highlight }"
          >
            {{ part.text }}
          </span>
        </span>
      </div>
    </div>
    <div
      v-else-if="searchQuery && !searchResults.length && showDropdown"
      class="no-results"
    >
      {{ noResultsMessage || 'No results found' }}
    </div>
  </div>
</template>


<script>
import axios from 'axios';
import { debounce } from 'lodash';

export default {
  props: ['otherPersonId', 'myPersonId', 'isPollingPaused', 'isOrdinaryUser'],
  name: 'SearchMessages',
  data() {
    return {
      searchQuery: '',
      searchResults: [],
      showDropdown: false,
      noResultsMessage: '',
      isSearching: false
    };
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('keydown', this.handleKeyDown);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    handleKeyDown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this.showDropdown = false;
      }
    },
    handleSearch: debounce(async function () {
      this.isSearching = true;
      if (!this.searchQuery.trim()) {
        this.searchResults = [];
        this.showDropdown = false;
        this.isSearching = false;
        return;
      }

      try {
        const response = await axios.get(`/api/v1/messages/message/${encodeURIComponent(this.searchQuery)}`, {
          params: {
            with: this.otherPersonId,
            chatPersonId: this.myPersonId,
          }
        });

        if (Array.isArray(response.data)) {
          this.searchResults = response.data;
          this.showDropdown = true;
          this.noResultsMessage = '';
        } else if (response.data.message) {
          this.searchResults = [];
          this.noResultsMessage = response.data.message;
          this.showDropdown = true;
        } else {
          this.searchResults = [];
          this.noResultsMessage = 'No results found.';
          this.showDropdown = true;
        }

        this.isSearching = false;
      } catch (error) {
        this.isSearching = false;
        this.searchResults = [];
        this.noResultsMessage = 'An error occurred during search.';
        this.showDropdown = true;
      }
    }, 300),

    highlightSearchTermParts(text) {
      if (!this.searchQuery || !text) return [{ text, highlight: false }];

      const query = this.searchQuery.trim();
      const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');

      const parts = [];
      let lastIndex = 0;

      text.replace(regex, (match, p1, offset) => {
        if (offset > lastIndex) {
          parts.push({ text: text.substring(lastIndex, offset), highlight: false });
        }
        parts.push({ text: match, highlight: true });
        lastIndex = offset + match.length;
      });

      if (lastIndex < text.length) {
        parts.push({ text: text.substring(lastIndex), highlight: false });
      }

      return parts;
    },

    escapeRegex(text) {
      return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    },

    selectMessage(messageId) {
      this.$emit('messageSelected', messageId);
      this.closeDropdown();
    },
    updateMessages() {
      this.$emit('updateMessages')
    },
    closeDropdown() {
      this.showDropdown = false;
      this.searchQuery = '';
      this.searchResults = [];
      this.noResultsMessage = '';
    },
    handleFocus() {
      if (this.searchQuery && this.searchResults.length) {
        this.showDropdown = true;
      }
    },
    handleOutsideClick(event) {
      const container = this.$refs.searchContainer;
      if (container && !container.contains(event.target)) {
        this.showDropdown = false;
      }
    },
  },
};
</script>

<style scoped>
.search-container {
  max-width: 400px;
  margin: 0 auto;
  padding: 1rem;
  position: relative;
}

.search-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
}

.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.result-item {
  padding: 0.5rem;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  display: flex;
  flex-direction: column;
}

.result-item:hover {
  background-color: #f5f5f5;
}

.result-item:last-child {
  border-bottom: none;
}

.sender {
  font-weight: bold;
  color: #333;
}

.message {
  color: #666;
}

.highlight {
  background-color: yellow;
  color: black;
}

.no-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 0.5rem;
  text-align: center;
  color: #666;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.input-wrapper {
  position: relative;
}

.clear-button {
  position: absolute;
  right: 8px;
  top: 40%;
  transform: translateY(-20%);
  background: transparent;
  border: none;
  font-size: 1.2rem;
  color: #3c425a;
  cursor: pointer;
  padding: 0;
}

.clear-button:hover {
  color: #004572;
}

.search-input {
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 0.5rem; /* right padding for button */
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
}
.lds-ripple,
.lds-ripple div {
  box-sizing: border-box;
}
.lds-ripple {
  display: inline-block;
  position: relative;
  width: 80px;
  height: 80px;
}
.lds-ripple div {
  position: absolute;
  border: 4px solid #243153;
  opacity: 1;
  border-radius: 50%;
  animation: lds-ripple 1s cubic-bezier(0, 0.2, 0.8, 1) infinite;
}
.lds-ripple div:nth-child(2) {
  animation-delay: -0.5s;
}
@keyframes lds-ripple {
  0% {
    top: 36px;
    left: 36px;
    width: 8px;
    height: 8px;
    opacity: 0;
  }
  4.9% {
    top: 36px;
    left: 36px;
    width: 8px;
    height: 8px;
    opacity: 0;
  }
  5% {
    top: 36px;
    left: 36px;
    width: 8px;
    height: 8px;
    opacity: 1;
  }
  100% {
    top: 0;
    left: 0;
    width: 80px;
    height: 80px;
    opacity: 0;
  }
}
.lds-grid p {
  color: #243153;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/SearchMessages.vue